<template>
  <div class="material-editable-list">
    <!-- 顶部按钮栏 -->
    <div class="editable-toolbar flex mb-10">
      <template v-if="!isEditing">
        <el-button type="primary" @click="onAdd">
          {{ hasNewRows ? '增加多条' : addText }}
        </el-button>
        <el-button v-if="!hasNewRows" type="primary" plain @click="onBatchEdit">
          批量编辑
        </el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDelete">
          删除
        </el-button>
        <el-button v-if="hasNewRows" type="primary" :loading="saveLoading" @click="onSave">
          保存
        </el-button>
        <el-button v-if="hasNewRows" @click="onCancelAdd">取消添加</el-button>
      </template>
      <template v-else>
        <el-button type="primary" plain @click="addRow">增加多条</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteRow">
          删除
        </el-button>
        <el-button type="primary" :loading="saveLoading" @click="onSave">保存</el-button>
        <el-button @click="onCancelEdit">取消编辑</el-button>
      </template>
    </div>

    <DataTableWithPagination
      v-model:page="page"
      v-model:page-size="pageSize"
      :data="tableRows"
      :loading="loading"
      :total="total"
      :show-column-settings="false"
      padding="0"
      size="small"
      :row-key="rowKey"
      :row-class-name="rowClassName"
      @page-change="loadList"
      @size-change="loadList"
      @selection-change="(rows: Record<string, unknown>[]) => (selectedRows = rows)"
      @row-click="onRowClick"
    >
      <!-- 批量编辑/删除选择列 -->
      <el-table-column type="selection" width="44" />

      <!-- 字段列（按 editable.fields 动态渲染） -->
      <el-table-column
        v-for="field in editable.fields"
        :key="field.prop"
        :label="field.label"
        :width="field.width"
      >
        <template #default="{ row }">
          <!-- 文本输入 -->
          <template v-if="field.type === 'input'">
            <el-input
              v-if="isRowEditable(row)"
              :model-value="row[field.prop] as string"
              :placeholder="field.placeholder ?? field.label"
              @update:model-value="(v: string) => (row[field.prop] = v)"
            />
            <span v-else>{{ row[field.prop] }}</span>
          </template>
          <!-- 图片上传 -->
          <template v-else-if="field.type === 'upload'">
            <el-upload
              v-if="isRowEditable(row)"
              :show-file-list="false"
              :auto-upload="false"
              accept="image/jpeg,image/png,image/jpg"
              @change="(f: { raw?: File }) => f.raw && onRowImageChange(row, field.prop, f.raw)"
            >
              <el-avatar shape="square" :size="32" :src="(row[field.prop] as string) || undefined">
                <el-icon><Plus /></el-icon>
              </el-avatar>
            </el-upload>
            <el-avatar
              v-else-if="row[field.prop]"
              shape="square"
              :size="32"
              :src="row[field.prop] as string"
            />
            <el-avatar v-else shape="square" :size="32">
              <el-icon><Picture /></el-icon>
            </el-avatar>
          </template>
        </template>
      </el-table-column>
    </DataTableWithPagination>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { Picture, Plus } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import { CHANNEL_MATERIAL_CONFIG } from '@/constants/channelMaterials';
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import { uploadImage } from '@/utils/upload';
import type { ChannelMaterialType, EditableConfig } from '@/types/channelMaterials';

type RawId = number | string;
type Row = Record<string, unknown> & { id?: number; isNew?: boolean };

const props = withDefaults(
  defineProps<{
    channelKey: string;
    materialType: ChannelMaterialType;
    accountId: number;
    selectedIds?: RawId[];
    selectionMode?: 'single' | 'multiple';
    showOrder?: boolean;
  }>(),
  {
    selectedIds: () => [],
    selectionMode: 'multiple',
    showOrder: false,
  },
);

const emit = defineEmits<{
  'update:selectedIds': [ids: RawId[]];
}>();

const configKey = computed(() => `${props.channelKey}:${props.materialType}`);
const editable = computed<EditableConfig>(() => {
  const cfg = CHANNEL_MATERIAL_CONFIG[configKey.value];
  if (!cfg?.editable) {
    throw new Error(`MaterialEditableList: no editable config for ${configKey.value}`);
  }
  return cfg.editable;
});

const rowKey = computed(() => CHANNEL_MATERIAL_CONFIG[configKey.value]?.rowKey ?? 'id');
const addText = computed(() => editable.value.addText ?? '新增');

// ── 列表状态 ──
const loading = ref(false);
const tableRows = ref<Row[]>([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(DEFAULT_PAGE_SIZE);

// ── 编辑状态 ──
const isEditing = ref(false);
const hasNewRows = computed(() => tableRows.value.some((r) => r.isNew));
const selectedRows = ref<Row[]>([]);
const saveLoading = ref(false);

async function loadList() {
  if (!props.accountId) {
    tableRows.value = [];
    total.value = 0;
    return;
  }
  loading.value = true;
  try {
    const { list, total: t } = await editable.value.load({
      accountId: props.accountId,
      page: page.value,
      pageSize: pageSize.value,
    });
    tableRows.value = list as Row[];
    total.value = t;
  } finally {
    loading.value = false;
  }
}

// 切渠道/类型/账号时重置编辑态并重新加载
watch(
  () => [props.channelKey, props.materialType, props.accountId],
  async () => {
    isEditing.value = false;
    selectedRows.value = [];
    page.value = 1;
    await nextTick();
    loadList();
  },
  { immediate: true },
);

// ── 增 / 删 / 改 ──

function onAdd() {
  tableRows.value.unshift(editable.value.emptyRow() as Row);
}

function onBatchEdit() {
  isEditing.value = true;
  selectedRows.value = [];
}

function addRow() {
  tableRows.value.push(editable.value.emptyRow() as Row);
}

function onDeleteRow() {
  // 编辑态下：从前端 tableRows 中移除（未保存的新增行直接丢弃；已存在行视为本地标记，提交时不会被改）
  // 因为 editedRows 只取 isEditing.value && row.id，移除已存在行相当于不更新，不会真的删后端数据
  // 编辑态的「删除」语义只针对新增行，这里和原弹框行为保持一致
  tableRows.value = tableRows.value.filter((r) => !selectedRows.value.includes(r));
  selectedRows.value = [];
}

async function onDelete() {
  if (selectedRows.value.length === 0) return;
  const ids = selectedRows.value.map((r) => r.id).filter((v): v is number => typeof v === 'number');
  if (ids.length === 0) {
    ElMessage.warning('请选择已保存的数据');
    return;
  }
  await ElMessageBox.confirm(editable.value.deleteConfirmText ?? '确定删除选中的物料吗？', '提示', {
    type: 'warning',
  });
  await editable.value.remove(ids);
  ElMessage.success('删除成功');
  selectedRows.value = [];
  // 已选物料中如果包含被删除的项，同步移除
  const removed = new Set<RawId>(ids);
  const next = props.selectedIds.filter((id) => !removed.has(id));
  if (next.length !== props.selectedIds.length) emit('update:selectedIds', next);
  loadList();
}

async function onRowImageChange(row: Row, prop: string, file: File) {
  const url = await uploadImage(file);
  if (url) row[prop] = url;
}

async function onSave() {
  // 收集可能为有效内容的行（任一必填字段非空）
  const requiredProps = editable.value.fields.filter((f) => f.required).map((f) => f.prop);
  const rows = tableRows.value.filter((r) => {
    if (!isRowEditable(r)) return false;
    return requiredProps.some((p) => String(r[p] ?? '').trim());
  });
  if (rows.length === 0) {
    ElMessage.warning('请至少填写一条数据');
    return;
  }
  const newRows = rows.filter((r) => r.isNew);
  const editedRows = isEditing.value ? rows.filter((r) => !r.isNew && r.id) : [];
  if (newRows.length === 0 && editedRows.length === 0) {
    isEditing.value = false;
    loadList();
    return;
  }
  // 字段校验
  const validateRow = (r: Row, idx: number, prefix: string): string | null => {
    for (const f of editable.value.fields) {
      const v = String(r[f.prop] ?? '').trim();
      if (f.required && !v) return `${prefix}第${idx + 1}条：${f.label}不能为空`;
      if (v && f.validate) {
        const err = f.validate(v, r);
        if (err) return `${prefix}第${idx + 1}条：${err}`;
      }
    }
    return null;
  };
  for (let i = 0; i < newRows.length; i++) {
    const err = validateRow(newRows[i]!, i, '新增');
    if (err) return ElMessage.warning(err);
  }
  for (let i = 0; i < editedRows.length; i++) {
    const err = validateRow(editedRows[i]!, i, '更新');
    if (err) return ElMessage.warning(err);
  }
  saveLoading.value = true;
  const failures: string[] = [];
  try {
    if (newRows.length > 0) {
      const res = await editable.value.create(newRows, props.accountId);
      failures.push(...res.failures);
    }
    if (editedRows.length > 0) {
      const res = await editable.value.update(editedRows, props.accountId);
      failures.push(...res.failures);
    }
    if (failures.length > 0) {
      ElMessage.warning({ message: failures.join('\n'), duration: 5000 });
    } else {
      ElMessage.success('保存成功');
      isEditing.value = false;
      selectedRows.value = [];
    }
    loadList();
  } finally {
    saveLoading.value = false;
  }
}

function onCancelEdit() {
  isEditing.value = false;
  loadList();
}

function onCancelAdd() {
  tableRows.value = tableRows.value.filter((r) => !r.isNew);
}

// ── 行可编辑判定 ──

function isRowEditable(row: Row) {
  return isEditing.value || !!row.isNew;
}

// ── 选物料（行点击）──

function getRowId(row: Row): RawId | undefined {
  const v = row[rowKey.value as keyof Row];
  return typeof v === 'number' || typeof v === 'string' ? v : undefined;
}

function isSelectedForPlan(row: Row) {
  const id = getRowId(row);
  return id !== undefined && props.selectedIds.includes(id);
}

function rowClassName({ row }: { row: Row }) {
  return isSelectedForPlan(row) ? 'is-plan-selected' : '';
}

// 行点击：编辑态/新增行不触发；点击选择列也不触发（@click.stop 已挡）
function onRowClick(row: Row, column: { type?: string }) {
  if (isEditing.value || row.isNew) return;
  if (column?.type === 'selection') return;
  const id = getRowId(row);
  if (id === undefined) return;
  let next: RawId[];
  if (props.selectionMode === 'single') {
    next = isSelectedForPlan(row) ? [] : [id];
  } else {
    next = isSelectedForPlan(row)
      ? props.selectedIds.filter((v) => v !== id)
      : [...props.selectedIds, id];
  }
  emit('update:selectedIds', next);
}
</script>

<style lang="scss" scoped>
.material-editable-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}

.editable-toolbar {
  gap: 8px;

  /* Element Plus 的 .el-button + .el-button 自带 20px 左边距，会与 gap 叠加成 28px，这里归零只保留 gap */
  .el-button + .el-button {
    margin-left: 0;
  }
}

:deep(.el-table .is-plan-selected) {
  background: rgba(26, 124, 255, 0.06);
}
</style>
